import { ApiError, type Profile } from "./client";

/**
 * The boot `getMe` handshake, hardened against a TRANSIENT network blip — the real cause of the
 * «Load failed» reports: a Mini App fetch to the direct origin can be reset / time out on a flaky
 * connection, and the app used to dump the user straight to the error screen on the FIRST failure.
 *
 * This retries a transient failure a few times with short backoff, each attempt bounded by a REAL
 * fetch timeout (an `AbortController`, so a hung request is actually cancelled — not just raced),
 * before giving up. Guarantees:
 *   - the FAST PATH is untouched: a getMe that resolves returns immediately, no delay, no extra
 *     await — the timer is armed then cleared;
 *   - a DETERMINISTIC failure (a 4xx: expired initData → the client already schedules a reload, a
 *     channel gate, a ban) is NOT retried — retrying an HTTP verdict just delays the real outcome;
 *   - a GENUINE, PERSISTENT failure still reaches the error screen, after a bounded number of tries
 *     (never an infinite loop).
 */

/** Retryable = a network-level failure (fetch rejection / our abort-timeout) or a 5xx server
 *  hiccup. A 4xx is a deterministic answer the server will just repeat, so it is NOT retried. */
export function isTransientBootError(error: unknown): boolean {
  if (error instanceof ApiError) return error.status >= 500;
  return true; // TypeError «Load failed»/«Failed to fetch», or the AbortError from our timeout
}

export interface ResilientBootOptions {
  /** Total attempts including the first. Every real caller passes its own policy — see
   *  {@link BOOT_GETME_RETRY} and {@link AVATAR_UPLOAD_RETRY}; these are only a generic fallback. */
  attempts?: number;
  /** Per-attempt abort timeout in ms. ⚠️ Pick it from the CALLER's measured latency, not from what
   *  a healthy request costs: a bound below the link's real latency cancels requests that were
   *  about to be answered (see the note on {@link BOOT_GETME_RETRY}). */
  timeoutMs?: number;
  /** Backoff before attempts 2, 3, … (ms). The last value repeats if there are more gaps. */
  backoffMs?: number[];
  /** Injectable sleep + AbortController (tests advance instantly / count aborts). */
  sleep?: (ms: number) => Promise<void>;
  makeController?: () => AbortController;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

const realSleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/**
 * Run ANY single api call with retry + per-attempt abort timeout. `run(init)` MUST forward
 * `init.signal` to its fetch (the api client does) or the abort cannot actually cancel anything.
 *
 * Extracted from `resilientGetMe` unchanged so a second caller could use it: the AVATAR UPLOAD,
 * where 10.6% of prod attempts died mid-TLS (`SSL_read() failed … bad record type`) and the client
 * gave up on the first try — while 33 client IPs proved that retrying by hand works.
 */
export async function resilientRequest<T>(
  run: (init?: { signal: AbortSignal }) => Promise<T>,
  opts: ResilientBootOptions = {},
): Promise<T> {
  const attempts = Math.max(1, opts.attempts ?? 3);
  const timeoutMs = opts.timeoutMs ?? 4000;
  const backoff = opts.backoffMs ?? [500, 1500];
  const sleep = opts.sleep ?? realSleep;
  const makeController = opts.makeController ?? (() => new AbortController());
  const setTimer = opts.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = opts.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));

  let lastError: unknown;
  for (let i = 0; i < attempts; i += 1) {
    const controller = makeController();
    const timer = setTimer(() => controller.abort(), timeoutMs);
    try {
      return await run({ signal: controller.signal });
    } catch (error) {
      lastError = error;
      // Stop early on a deterministic failure, or once the tries are spent — the error propagates
      // to the boot catch (which shows the Persian offline message).
      if (!isTransientBootError(error) || i === attempts - 1) throw error;
      await sleep(backoff[i] ?? backoff[backoff.length - 1] ?? 1000);
    } finally {
      clearTimer(timer);
    }
  }
  throw lastError; // unreachable (the loop always returns or throws), keeps TS happy
}

/**
 * Retry policy for the BOOT `getMe` handshake. Deliberately NOT the generic defaults, for the same
 * reason {@link AVATAR_UPLOAD_RETRY} isn't: this request crosses a network that is SLOW before it
 * is broken, and a bound shorter than the link's real latency cancels a request that was about to
 * be answered.
 *
 * MEASURED on the prod origin (2026-08-25, 2,433 cold boots): the time from the entry bundle
 * landing to `/api/me` 200 is p50 1s · p90 5s · p95 8s · **p99 22s** — the old 4s bound sat below
 * the p90 of the whole handshake. In the reported case the user's link answered two SIBLING boot
 * requests (fired in the same tick, but not abort-bounded) at 18s and 30s, while all three getMe
 * attempts were cancelled by us at 4s and never reached the origin at all.
 *
 * - **20s per attempt** covers that measured p99 with room, so a slow-but-working link is WAITED
 *   for instead of being cut off.
 * - **Two attempts, not three.** The origin is HTTP/1.1 (~6 connections per origin) and a cold
 *   shell saturates them, so every abort hands the slot back and re-queues the retry BEHIND the
 *   shell: extra impatient attempts deepen the starvation rather than escaping it. Retries still
 *   cover the case they were written for — a RESET connection — which fails fast, so that retry
 *   still goes out almost immediately and costs nothing.
 * - **41s worst case**: bounded (never an infinite spinner), and far inside the 135s this app
 *   already spends retrying a dropped GAME connection. Nobody is staring at a blank screen while
 *   it runs — the branded splash shows a progress bar throughout and, from `STALL_MS` (8s), says
 *   «کمی طول کشیده؛ لطفاً صبر کنید…».
 */
export const BOOT_GETME_RETRY: ResilientBootOptions = {
  attempts: 2,
  timeoutMs: 20_000,
  backoffMs: [1_000],
};

/** The boot `getMe` handshake — {@link resilientRequest} under {@link BOOT_GETME_RETRY}. */
export function resilientGetMe(
  getMe: (init?: { signal: AbortSignal }) => Promise<Profile>,
  opts: ResilientBootOptions = {},
): Promise<Profile> {
  return resilientRequest(getMe, { ...BOOT_GETME_RETRY, ...opts });
}

/**
 * Retry policy for the AVATAR UPLOAD. Its own policy, like {@link BOOT_GETME_RETRY}:
 *
 * - the per-attempt bound is 10s. An upload carries a body over a network that is slow before it is
 *   broken, and a short abort would cancel uploads that were about to succeed — turning a fix into
 *   a regression. It still sits far under nginx's 60s `client_body_timeout`. (It is SHORTER than
 *   the boot's 20s on purpose: a failed upload surfaces one retryable toast inside a working app,
 *   whereas a failed boot is the difference between getting in and not.)
 * - the backoff is short because the failure we are retrying (a severed TLS record) fails FAST,
 *   so in the common case the retry goes out almost immediately.
 *
 * A duplicate upload from a retry is harmless: each one mints a new Avatar row that supersedes the
 * last, and the server's 60s in-flight grace already exists to keep two of them from racing.
 */
export const AVATAR_UPLOAD_RETRY: ResilientBootOptions = {
  attempts: 3,
  timeoutMs: 10_000,
  backoffMs: [400, 1200],
};
